import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import {
    Award,
    Languages,
    Stethoscope,
    MapPin,
    Briefcase,
    GraduationCap,
    ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/medical/Navbar";
import { Footer } from "../components/medical/Footer";
import { WhatsappFloat } from "../components/medical/WhatsappFloat";
import { Breadcrumbs } from "../components/medical/Breadcrumbs";
import { SEO } from "../components/medical/SEO";
import { ConsultationForm } from "../components/medical/ConsultationForm";
import { getDoctor, DOCTORS } from "../data/doctors";
import { getSpecialty } from "../data/specialties";
import { getTreatment } from "../data/treatments";

export const DoctorProfile = () => {
    const { slug } = useParams();
    const doctor = getDoctor(slug);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [slug]);

    if (!doctor) return <Navigate to="/" replace />;

    const specialty = getSpecialty(doctor.specialty);
    const treatments = (doctor.treatments || [])
        .map(getTreatment)
        .filter(Boolean);
    const related = DOCTORS.filter(
        (d) => d.slug !== doctor.slug && d.specialty === doctor.specialty
    ).slice(0, 4);

    const schema = {
        "@context": "https://schema.org",
        "@type": "Physician",
        name: doctor.name,
        image: doctor.image,
        medicalSpecialty: specialty?.shortName || doctor.specialtyLabel,
        worksFor: { "@type": "Hospital", name: doctor.hospital },
        knowsLanguage: doctor.languages,
        alumniOf: doctor.qualification,
    };

    return (
        <div className="min-h-screen bg-background font-body">
            <SEO
                title={`${doctor.name} — ${doctor.specialtyLabel}`}
                description={`Book a free consultation with ${doctor.name}, ${doctor.specialtyLabel} at ${doctor.hospital}. ${doctor.experience} of experience.`}
                image={doctor.image}
                schema={schema}
            />
            <Navbar />
            <main className="pt-32 md:pt-40">
                {/* Header */}
                <section className="relative bg-gradient-to-br from-brand-50/60 via-white to-orange-50/40 py-12 md:py-16 border-b border-stone-200">
                    <div className="mx-auto max-w-7xl px-5 sm:px-8">
                        <Breadcrumbs
                            items={[
                                { label: "Doctors", to: "/#doctors" },
                                {
                                    label: specialty?.shortName || "Specialty",
                                    to: specialty
                                        ? `/specialties/${specialty.slug}`
                                        : "/",
                                },
                                { label: doctor.name },
                            ]}
                        />

                        <div className="mt-8 grid lg:grid-cols-12 gap-10 items-start">
                            <div className="lg:col-span-4">
                                <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-white shadow-xl border border-stone-200">
                                    <img
                                        src={doctor.image}
                                        alt={doctor.name}
                                        className="w-full h-full object-cover object-top"
                                    />
                                </div>
                            </div>
                            <div className="lg:col-span-8">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-semibold tracking-wide">
                                    <Award className="h-3.5 w-3.5" />
                                    Empanelled Specialist
                                </div>
                                <h1
                                    data-testid="doctor-name"
                                    className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 leading-[1.05]"
                                >
                                    {doctor.name}
                                </h1>
                                <div className="mt-3 text-lg text-brand-700 font-semibold">
                                    {doctor.specialtyLabel}
                                </div>
                                <p className="mt-5 text-stone-600 leading-relaxed max-w-2xl">
                                    {doctor.bio}
                                </p>

                                <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-2xl">
                                    <div className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-stone-200">
                                        <GraduationCap className="h-5 w-5 text-brand-700 mt-0.5" />
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-stone-500">
                                                Qualifications
                                            </div>
                                            <div className="text-sm font-semibold text-stone-900">
                                                {doctor.qualification}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-stone-200">
                                        <Briefcase className="h-5 w-5 text-brand-700 mt-0.5" />
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-stone-500">
                                                Experience
                                            </div>
                                            <div className="text-sm font-semibold text-stone-900">
                                                {doctor.experience}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-stone-200">
                                        <MapPin className="h-5 w-5 text-brand-700 mt-0.5" />
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-stone-500">
                                                Hospital
                                            </div>
                                            <div className="text-sm font-semibold text-stone-900">
                                                {doctor.hospital}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-stone-200">
                                        <Stethoscope className="h-5 w-5 text-brand-700 mt-0.5" />
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-stone-500">
                                                Designation
                                            </div>
                                            <div className="text-sm font-semibold text-stone-900">
                                                {doctor.designation}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 flex flex-wrap gap-3">
                                    <Button
                                        asChild
                                        data-testid="doctor-cta-consult"
                                        className="rounded-full bg-orange-500 hover:bg-orange-600 text-white px-7 h-12 text-base shadow-lg hover:-translate-y-0.5 transition-all"
                                    >
                                        <a href="#book">Book Free Consultation</a>
                                    </Button>
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="rounded-full border-stone-300 hover:bg-stone-50 px-7 h-12"
                                    >
                                        <a
                                            href={`https://wa.me/917982959943?text=${encodeURIComponent(
                                                `Hi TruHeal India, I'd like to consult ${doctor.name}.`
                                            )}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Chat on WhatsApp
                                        </a>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Expertise + Treatments + Languages + Awards */}
                <section className="py-16 md:py-24">
                    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-8 space-y-12">
                            {/* Expertise */}
                            <div>
                                <h2 className="font-display text-3xl font-bold text-stone-900">
                                    Areas of Expertise
                                </h2>
                                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                                    {doctor.expertise.map((e) => (
                                        <div
                                            key={e}
                                            className="flex items-start gap-3 p-4 rounded-xl bg-brand-50/50 border border-brand-100"
                                        >
                                            <span className="h-2 w-2 mt-2 rounded-full bg-orange-500" />
                                            <span className="text-sm font-medium text-stone-800">
                                                {e}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Treatments */}
                            {treatments.length > 0 && (
                                <div>
                                    <h2 className="font-display text-3xl font-bold text-stone-900">
                                        Treatments & Surgeries Performed
                                    </h2>
                                    <div className="mt-6 grid sm:grid-cols-2 gap-4">
                                        {treatments.map((t) => (
                                            <Link
                                                key={t.slug}
                                                to={`/treatments/${t.slug}`}
                                                data-testid={`doctor-treatment-${t.slug}`}
                                                className="group flex items-center justify-between p-5 rounded-2xl bg-white border border-stone-200 hover:border-brand-400 hover:shadow-md transition-all"
                                            >
                                                <div>
                                                    <div className="font-display font-semibold text-stone-900">
                                                        {t.shortName}
                                                    </div>
                                                    <div className="text-xs text-stone-500 mt-1">
                                                        {t.tagline}
                                                    </div>
                                                </div>
                                                <ArrowRight className="h-4 w-4 text-stone-400 group-hover:text-brand-700 group-hover:translate-x-0.5 transition-all" />
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Languages */}
                            <div>
                                <h2 className="font-display text-3xl font-bold text-stone-900">
                                    Languages Spoken
                                </h2>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {doctor.languages.map((l) => (
                                        <span
                                            key={l}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-50 text-brand-700 text-sm font-medium border border-brand-100"
                                        >
                                            <Languages className="h-3.5 w-3.5" />
                                            {l}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Awards */}
                            {doctor.awards && doctor.awards.length > 0 && (
                                <div>
                                    <h2 className="font-display text-3xl font-bold text-stone-900">
                                        Awards & Achievements
                                    </h2>
                                    <ul className="mt-6 space-y-3">
                                        {doctor.awards.map((a, i) => (
                                            <li
                                                key={i}
                                                className="flex items-start gap-4 p-4 rounded-xl bg-white border border-stone-200"
                                            >
                                                <span className="inline-flex h-9 w-9 rounded-full bg-orange-100 text-orange-700 items-center justify-center text-xs font-bold">
                                                    {a.year}
                                                </span>
                                                <span className="text-sm text-stone-800 font-medium pt-1.5">
                                                    {a.title}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Sticky right column — consultation form */}
                        <div className="lg:col-span-4">
                            <div id="book" className="lg:sticky lg:top-32">
                                <ConsultationForm
                                    context={`Doctor: ${doctor.name}`}
                                    treatmentSuggestion={
                                        treatments[0]?.shortName || "Other"
                                    }
                                    title={`Consult ${doctor.name.split(" ")[1] ? "Dr. " + doctor.name.split(" ").slice(-1)[0] : doctor.name}`}
                                    subtitle="Share your reports — a coordinator will arrange a doctor's opinion within 24 hours."
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Related Doctors */}
                {related.length > 0 && (
                    <section className="py-16 md:py-20 bg-stone-50/60 border-t border-stone-200">
                        <div className="mx-auto max-w-7xl px-5 sm:px-8">
                            <h2 className="font-display text-3xl font-bold text-stone-900">
                                Related Doctors
                            </h2>
                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {related.map((d) => (
                                    <Link
                                        key={d.slug}
                                        to={`/doctors/${d.slug}`}
                                        data-testid={`related-doctor-${d.slug}`}
                                        className="group rounded-3xl bg-white border border-stone-200 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col"
                                    >
                                        <div className="aspect-[4/5] overflow-hidden bg-stone-100">
                                            <img
                                                src={d.image}
                                                alt={d.name}
                                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-5">
                                            <div className="text-[11px] uppercase tracking-wider text-brand-700 font-semibold">
                                                {d.specialtyLabel}
                                            </div>
                                            <div className="font-display text-lg font-bold text-stone-900 mt-1">
                                                {d.name}
                                            </div>
                                            <div className="text-xs text-stone-500 mt-1">
                                                {d.experience}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </main>
            <Footer />
            <WhatsappFloat />
        </div>
    );
};
